import { motion } from "framer-motion";
import { Users, Search, Coins, Ban, UserCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  avatar_url: string | null;
  duv_coins: number;
  total_wins: number;
  total_bids: number;
  is_banned: boolean;
  created_at: string;
  email?: string;
  signup_location?: string | null;
}

interface AdminUsersProps {
  profiles: Profile[];
  totalUsers: number;
  isLoading: boolean;
  userSearch: string;
  setUserSearch: (search: string) => void;
  userPage: number;
  setUserPage: (page: number) => void;
  usersPerPage: number;
  adjustingUserId: string | null;
  coinAdjustment: Record<string, string>;
  setCoinAdjustment: (adjustment: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  onAdjustCoins: (userId: string) => void;
  onToggleBan: (userId: string, currentlyBanned: boolean) => void;
}

const AdminUsers = ({
  profiles,
  totalUsers,
  isLoading,
  userSearch,
  setUserSearch,
  userPage,
  setUserPage,
  usersPerPage,
  adjustingUserId,
  coinAdjustment,
  setCoinAdjustment,
  onAdjustCoins,
  onToggleBan,
}: AdminUsersProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
          <Users size={20} className="text-primary" />
          User Management ({profiles.length})
        </h2>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No users found</div>
        ) : (
          profiles.map((profile) => (
            <div
              key={profile.id}
              className={`p-3 sm:p-4 rounded-lg border ${
                profile.is_banned 
                  ? "bg-destructive/10 border-destructive/30" 
                  : "bg-muted/30 border-border/50"
              }`}
            >
              <div className="flex flex-col gap-3">
                {/* User Info Row */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-primary/20 flex items-center justify-center">
                    {profile.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <Users size={18} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground text-sm sm:text-base">
                        {profile.username || "Anonymous"}
                      </span>
                      {profile.is_banned && (
                        <span className="px-2 py-0.5 text-xs bg-destructive/20 text-destructive rounded">
                          BANNED
                        </span>
                      )}
                    </div>
                    {profile.email && (
                      <p className="text-xs text-primary truncate">
                        {profile.email}
                      </p>
                    )}
                    {profile.signup_location && (
                      <p className="text-xs text-muted-foreground truncate">
                        📍 {profile.signup_location}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground hidden sm:block truncate">
                      {profile.user_id}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {profile.duv_coins.toLocaleString()} DC | {profile.total_wins} wins | {profile.total_bids} predictions
                    </p>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/30 sm:border-0 sm:pt-0">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      placeholder="+/-"
                      className="w-16 sm:w-20 h-8 text-sm"
                      value={coinAdjustment[profile.user_id] || ""}
                      onChange={(e) => setCoinAdjustment({
                        ...coinAdjustment,
                        [profile.user_id]: e.target.value
                      })}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onAdjustCoins(profile.user_id)}
                      disabled={adjustingUserId === profile.user_id}
                    >
                      {adjustingUserId === profile.user_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Coins size={14} />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant={profile.is_banned ? "success" : "danger"}
                    size="sm"
                    onClick={() => onToggleBan(profile.user_id, profile.is_banned)}
                  >
                    {profile.is_banned ? (
                      <UserCheck size={14} />
                    ) : (
                      <Ban size={14} />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-border/50">
        <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
          Showing {userPage * usersPerPage + 1} - {Math.min((userPage + 1) * usersPerPage, totalUsers)} of {totalUsers}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUserPage(userPage - 1)}
            disabled={userPage === 0}
          >
            Prev
          </Button>
          <span className="flex items-center px-2 text-xs sm:text-sm text-muted-foreground">
            {userPage + 1}/{Math.ceil(totalUsers / usersPerPage)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUserPage(userPage + 1)}
            disabled={(userPage + 1) * usersPerPage >= totalUsers}
          >
            Next
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminUsers;
